import React, { Component } from "react";
import {
  withStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CameraAlt as CameraAltIcon,
} from "@material-ui/icons";
import { Html5Qrcode } from "html5-qrcode";

const styles = (theme) => ({
  dialogPaper: {
    maxWidth: 600,
    width: "90%",
    margin: theme.spacing(2),
    borderRadius: 16,
    [theme.breakpoints.down("sm")]: {
      width: "95%",
      margin: theme.spacing(1),
      borderRadius: 12,
    },
  },
  dialogTitle: {
    padding: theme.spacing(2, 3),
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: "1px solid #e2e8f0",
  },
  titleText: {
    fontWeight: 600,
    fontSize: "1.25rem",
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  dialogContent: {
    padding: theme.spacing(3),
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(2),
    },
  },
  scannerContainer: {
    position: "relative",
    width: "100%",
    maxWidth: 500,
    margin: "0 auto",
    borderRadius: 12,
    overflow: "hidden",
    backgroundColor: "#000",
    aspectRatio: "1/1", // Force square on all devices
    [theme.breakpoints.down("sm")]: {
      aspectRatio: "1/1", // Ensure square on mobile
      maxWidth: "100%",
    },
  },
  videoElement: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  overlay: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    gap: theme.spacing(2),
    background: "rgba(0, 0, 0, 0.5)",
    color: "#fff",
    zIndex: 1,
  },
  scannerFrame: {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: "70%",
    height: "70%",
    border: "3px solid #fff",
    borderRadius: 12,
    boxShadow: "0 0 0 9999px rgba(0, 0, 0, 0.5)",
    "&::before, &::after": {
      content: '""',
      position: "absolute",
      width: 20,
      height: 20,
      border: "3px solid #4caf50",
    },
    "&::before": {
      top: -3,
      left: -3,
      borderRight: "none",
      borderBottom: "none",
    },
    "&::after": {
      top: -3,
      right: -3,
      borderLeft: "none",
      borderBottom: "none",
    },
  },
  instructions: {
    textAlign: "center",
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
    fontSize: "0.95rem",
  },
  resultContainer: {
    textAlign: "center",
    padding: theme.spacing(3),
  },
  successIcon: {
    fontSize: 64,
    color: "#4caf50",
    marginBottom: theme.spacing(2),
  },
  errorIcon: {
    fontSize: 64,
    color: "#f44336",
    marginBottom: theme.spacing(2),
  },
  attendeeDetails: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: "#f8fafc",
    borderRadius: 8,
    textAlign: "left",
  },
  detailRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(1, 0),
    "&:not(:last-child)": {
      borderBottom: "1px solid #e2e8f0",
    },
  },
  detailLabel: {
    fontWeight: 600,
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  detailValue: {
    color: theme.palette.text.primary,
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  phoneText: {
    fontFamily: "monospace",
  },
  statusChip: {
    fontWeight: 600,
  },
});

class QRScanner extends Component {
  constructor(props) {
    super(props);
    this.state = {
      scanning: false,
      processing: false,
      result: null,
      error: null,
      cameraPermission: null,
      scannedData: null, // Store scanned QR data
      attendeeDetails: null, // Store fetched attendee details
      showConfirmation: false, // Show confirmation dialog
    };
    this.html5QrCode = null;
    this.scannerId = "qr-scanner-region";
  }

  componentDidUpdate(prevProps) {
    if (this.props.open && !prevProps.open) {
      this.startScanning();
    } else if (!this.props.open && prevProps.open) {
      this.stopScanning();
    }
  }

  componentWillUnmount() {
    this.stopScanning();
  }

  startScanning = async () => {
    try {
      this.setState({ scanning: true, result: null, error: null });

      // Check camera permission
      if (navigator.permissions) {
        const permissionStatus = await navigator.permissions.query({
          name: "camera",
        });
        this.setState({ cameraPermission: permissionStatus.state });
      }

      // Initialize scanner
      this.html5QrCode = new Html5Qrcode(this.scannerId);

      const config = {
        fps: 10,
        qrbox: { width: 250, height: 250 },
        aspectRatio: 1.0,
      };

      await this.html5QrCode.start(
        { facingMode: "environment" }, // Use back camera
        config,
        this.onScanSuccess,
        this.onScanError
      );
    } catch (err) {
      console.error("Error starting scanner:", err);
      this.setState({
        error:
          err.name === "NotAllowedError"
            ? "Camera permission denied. Please allow camera access to scan QR codes."
            : "Failed to start camera. Please ensure camera permissions are granted.",
        scanning: false,
      });
    }
  };

  stopScanning = async () => {
    try {
      if (this.html5QrCode && this.html5QrCode.isScanning) {
        await this.html5QrCode.stop();
        this.html5QrCode.clear();
      }
    } catch (err) {
      console.error("Error stopping scanner:", err);
    }
    this.setState({ scanning: false });
  };

  onScanSuccess = async (decodedText, decodedResult) => {
    try {
      // Stop scanning immediately
      await this.stopScanning();

      this.setState({ processing: true, error: null });

      // Parse QR code data
      let qrData;
      try {
        qrData = JSON.parse(decodedText);
      } catch (e) {
        // If not JSON, treat as plain text
        qrData = { tid: decodedText };
      }

      if (!qrData.tid) {
        this.setState({
          error: "Invalid QR code format. Ticket ID not found.",
          processing: false,
        });
        return;
      }

      // Fetch attendee details without checking in
      await this.fetchAttendeeDetails(decodedText);
      this.setState({
        processing: false,
        scannedData: decodedText,
        showConfirmation: true,
      });
    } catch (err) {
      console.error("Error processing QR code:", err);
      this.setState({
        error: err.message || "Failed to process QR code",
        processing: false,
      });
    }
  };

  onScanError = (errorMessage) => {
    // Ignore common scanning errors (these are expected while scanning)
    if (
      errorMessage.includes("No MultiFormat Readers") ||
      errorMessage.includes("NotFoundException")
    ) {
      return;
    }
    console.warn("QR Scan Error:", errorMessage);
  };

  fetchAttendeeDetails = async (qrCode) => {
    try {
      // Parse QR code to get ticket ID
      let qrData;
      try {
        qrData = JSON.parse(qrCode);
      } catch (e) {
        qrData = { tid: qrCode };
      }

      // Fetch attendee details from backend
      const response = await this.props.fetchAttendeeByQR(qrData.tid);

      if (response && response.success) {
        this.setState({
          attendeeDetails: response.data,
          error: null,
        });
      } else {
        const errorMessage =
          response?.message || "Failed to fetch attendee details";
        this.setState({
          error: errorMessage,
          attendeeDetails: null,
        });
        throw new Error(errorMessage);
      }
    } catch (err) {
      console.error("Error fetching attendee details:", err);
      const errorMessage = err.message || "Failed to fetch attendee details";
      this.setState({
        error: errorMessage,
        attendeeDetails: null,
      });
      throw err;
    }
  };

  handleConfirmCheckIn = async () => {
    try {
      this.setState({ processing: true, error: null });

      // Call the original check-in API
      await this.props.onScan(this.state.scannedData);

      this.setState({
        processing: false,
        showConfirmation: false,
      });
    } catch (err) {
      console.error("Error during check-in:", err);
      this.setState({
        error: err.message || "Failed to check-in",
        processing: false,
      });
    }
  };

  handleCancelCheckIn = () => {
    // Close the entire dialog instead of restarting scanner
    this.handleClose();
  };

  handleClose = () => {
    this.stopScanning();
    this.setState({
      showConfirmation: false,
      scannedData: null,
      attendeeDetails: null,
      error: null,
      result: null,
    });
    this.props.onClose();
  };

  handleRetry = () => {
    this.setState({
      error: null,
      result: null,
      showConfirmation: false,
      scannedData: null,
      attendeeDetails: null,
    });
    this.startScanning();
  };

  renderAttendeeDetails = (data, alreadyCheckedIn) => {
    const { classes } = this.props;

    return (
      <Box className={classes.attendeeDetails}>
        {alreadyCheckedIn && (
          <Box mb={2} textAlign="center">
            <Chip
              label="Already Checked In"
              color="primary"
              className={classes.statusChip}
            />
          </Box>
        )}

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Name:</span>
          <span className={classes.detailValue}>{data.attendeeName}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Email:</span>
          <span className={classes.detailValue}>{data.email}</span>
        </div>

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Phone:</span>
          <span className={`${classes.detailValue} ${classes.phoneText}`}>
            {data.countryCallingCode ? `${data.countryCallingCode} ` : ""}
            {data.phone}
          </span>
        </div>

        {data.organizationName && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Organization:</span>
            <span className={classes.detailValue}>{data.organizationName}</span>
          </div>
        )}

        {data.designation && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Designation:</span>
            <span className={classes.detailValue}>{data.designation}</span>
          </div>
        )}

        {data.industry && (
          <div className={classes.detailRow}>
            <span className={classes.detailLabel}>Industry:</span>
            <span className={classes.detailValue}>{data.industry}</span>
          </div>
        )}

        <div className={classes.detailRow}>
          <span className={classes.detailLabel}>Ticket Type:</span>
          <span className={classes.detailValue}>{data.ticketType}</span>
        </div>
      </Box>
    );
  };

  render() {
    const { classes, open, result, alreadyCheckedIn } = this.props;
    const { scanning, processing, error, showConfirmation, attendeeDetails } =
      this.state;

    return (
      <Dialog
        open={open}
        onClose={this.handleClose}
        maxWidth="md"
        fullWidth
        classes={{ paper: classes.dialogPaper }}
        disableBackdropClick={scanning || processing}
        disableEscapeKeyDown={scanning || processing}
      >
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6" className={classes.titleText}>
            <CameraAltIcon />
            QR Code Scanner
          </Typography>
          <IconButton
            size="small"
            onClick={this.handleClose}
            disabled={processing}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          {/* Scanning State */}
          {scanning && !result && !error && !showConfirmation && (
            <>
              <div id={this.scannerId} className={classes.scannerContainer} />
              <Typography className={classes.instructions}>
                Position the QR code within the frame to scan
              </Typography>
            </>
          )}

          {/* Processing State */}
          {processing && !showConfirmation && (
            <Box className={classes.resultContainer}>
              <CircularProgress size={64} />
              <Typography variant="h6" style={{ marginTop: 16 }}>
                Processing...
              </Typography>
            </Box>
          )}

          {/* Confirmation State - Show attendee details before check-in */}
          {showConfirmation && attendeeDetails && !result && (
            <Box className={classes.resultContainer}>
              {attendeeDetails.isCheckedIn ? (
                <>
                  <CheckCircleIcon
                    style={{ fontSize: 64, color: "#10b981", marginBottom: 16 }}
                  />
                  <Typography
                    variant="h5"
                    gutterBottom
                    style={{ color: "#10b981", fontWeight: 600 }}
                  >
                    Already Checked In
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: 24 }}
                  >
                    This attendee has already been checked in
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="h5"
                    gutterBottom
                    style={{ color: "#667eea", fontWeight: 600 }}
                  >
                    Confirm Check-in
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    style={{ marginBottom: 24 }}
                  >
                    Please review attendee details before checking in
                  </Typography>
                </>
              )}

              <Box className={classes.attendeeDetails}>
                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Name:</span>
                  <span className={classes.detailValue}>
                    {attendeeDetails.fullName || attendeeDetails.attendeeName}
                  </span>
                </div>

                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Email:</span>
                  <span className={classes.detailValue}>
                    {attendeeDetails.email}
                  </span>
                </div>

                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Phone:</span>
                  <span
                    className={`${classes.detailValue} ${classes.phoneText}`}
                  >
                    {attendeeDetails.countryCallingCode
                      ? `${attendeeDetails.countryCallingCode} `
                      : ""}
                    {attendeeDetails.phone}
                  </span>
                </div>

                {attendeeDetails.organizationName && (
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Organization:</span>
                    <span className={classes.detailValue}>
                      {attendeeDetails.organizationName}
                    </span>
                  </div>
                )}

                {attendeeDetails.designation && (
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Designation:</span>
                    <span className={classes.detailValue}>
                      {attendeeDetails.designation}
                    </span>
                  </div>
                )}

                {attendeeDetails.industry && (
                  <div className={classes.detailRow}>
                    <span className={classes.detailLabel}>Industry:</span>
                    <span className={classes.detailValue}>
                      {attendeeDetails.industry}
                    </span>
                  </div>
                )}

                <div className={classes.detailRow}>
                  <span className={classes.detailLabel}>Ticket Type:</span>
                  <span className={classes.detailValue}>
                    {attendeeDetails.ticketTitle || attendeeDetails.ticketType}
                  </span>
                </div>

                {attendeeDetails.checkInDetails?.checkedInAt && (
                  <>
                    <Divider style={{ margin: "16px 0" }} />
                    <div className={classes.detailRow}>
                      <span className={classes.detailLabel}>
                        Checked-in At:
                      </span>
                      <span className={classes.detailValue}>
                        {new Date(
                          attendeeDetails.checkInDetails.checkedInAt
                        ).toLocaleString()}
                      </span>
                    </div>
                    {attendeeDetails.checkInDetails.checkedInBy && (
                      <div className={classes.detailRow}>
                        <span className={classes.detailLabel}>
                          Checked-in By:
                        </span>
                        <span className={classes.detailValue}>
                          {attendeeDetails.checkInDetails.checkedInBy}
                        </span>
                      </div>
                    )}
                  </>
                )}
              </Box>
            </Box>
          )}

          {/* Success State */}
          {result && result.success && !showConfirmation && (
            <Box className={classes.resultContainer}>
              <CheckCircleIcon className={classes.successIcon} />
              <Typography variant="h5" gutterBottom>
                {alreadyCheckedIn
                  ? "Already Checked In"
                  : "Check-in Successful!"}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {result.message}
              </Typography>
              {result.data &&
                this.renderAttendeeDetails(result.data, alreadyCheckedIn)}
            </Box>
          )}

          {/* Error State */}
          {(error || (result && !result.success)) && !showConfirmation && (
            <Box className={classes.resultContainer}>
              <ErrorIcon className={classes.errorIcon} />
              <Typography variant="h5" gutterBottom>
                Scan Failed
              </Typography>
              <Typography variant="body1" color="error">
                {error || result?.message || "An error occurred"}
              </Typography>
            </Box>
          )}
        </DialogContent>

        <DialogActions style={{ padding: "16px 24px" }}>
          {showConfirmation && attendeeDetails ? (
            <>
              {attendeeDetails.isCheckedIn ? (
                // If already checked in, show only Close button
                <Button
                  onClick={this.handleCancelCheckIn}
                  color="primary"
                  variant="contained"
                  style={{
                    backgroundColor: "#10b981",
                    color: "#fff",
                  }}
                >
                  Close
                </Button>
              ) : (
                // If not checked in, show Cancel and Confirm buttons
                <>
                  <Button
                    onClick={this.handleCancelCheckIn}
                    color="default"
                    disabled={processing}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={this.handleConfirmCheckIn}
                    color="primary"
                    variant="contained"
                    disabled={processing}
                  >
                    {processing ? "Checking In..." : "Confirm Check-in"}
                  </Button>
                </>
              )}
            </>
          ) : result || error ? (
            <>
              <Button onClick={this.handleClose} color="default">
                Close
              </Button>
            </>
          ) : (
            <Button
              onClick={this.handleClose}
              color="default"
              disabled={processing}
            >
              Cancel
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  }
}

export default withStyles(styles)(QRScanner);
